import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MessagesSquare,
  ArrowLeft,
  Users,
  UserPlus,
  Settings,
  Send,
  Paperclip,
  Image as ImageIcon,
  FileText,
  Video,
  Music,
  Download,
  Trash2,
  Edit2,
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  MoreVertical,
  Search,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  UserCheck,
  UserX,
  RefreshCw,
  ExternalLink,
  Eye,
  File as FileIcon,
  X,
  Lock,
  ChevronRight,
  Sparkles
} from "lucide-react";
import {
  useGetChatGroupQuery,
  useGetGroupMessagesQuery,
  useSendMessageMutation,
  useEditMessageMutation,
  useDeleteMessageMutation,
  useGetGroupMembersQuery,
  useAddMembersToGroupMutation,
  useUpdateMemberStatusMutation,
  useRemoveMemberMutation,
  useDeleteAllMemberMessagesMutation,
  useUpdateChatGroupMutation,
  useDeleteChatGroupMutation,
  ChatMessage,
  ChatMember
} from "@/redux/slices/chatApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { useGetStudentsQuery, Student } from "@/redux/slices/studentApi";
import { useUploadFileMutation, useUploadImageMutation } from "@/redux/slices/uploadApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn, getFileUrl } from "@/lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";

export default function ChatGroupDetails() {
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("chat");

  // Queries
  const { data: groupData, isLoading: isGroupLoading, refetch: refetchGroup } = useGetChatGroupQuery(groupId || "", {
    skip: !groupId,
    pollingInterval: 15000,
  });

  const { data: messagesData, isLoading: isMessagesLoading, refetch: refetchMessages } = useGetGroupMessagesQuery(
    { groupId: groupId || "", limit: 100 },
    {
      skip: !groupId,
      pollingInterval: 2000, // Reactive chat polling every 2s without page refresh
    }
  );

  const [memberSearch, setMemberSearch] = useState("");
  const [memberStatusFilter, setMemberStatusFilter] = useState("all");
  const [memberPage, setMemberPage] = useState(1);

  const { data: membersData, isLoading: isMembersLoading, refetch: refetchMembers } = useGetGroupMembersQuery(
    {
      groupId: groupId || "",
      search: memberSearch,
      status: memberStatusFilter,
      page: memberPage,
      limit: 20
    },
    { skip: !groupId }
  );

  // Mutations
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [editMessage, { isLoading: isEditingMsg }] = useEditMessageMutation();
  const [deleteMessage, { isLoading: isDeletingMessage }] = useDeleteMessageMutation();
  const [updateMemberStatus] = useUpdateMemberStatusMutation();
  const [removeMember] = useRemoveMemberMutation();
  const [deleteAllMemberMessages] = useDeleteAllMemberMessagesMutation();
  const [updateGroup, { isLoading: isUpdatingGroup }] = useUpdateChatGroupMutation();
  const [deleteGroup, { isLoading: isDeletingGroup }] = useDeleteChatGroupMutation();
  const [uploadFile, { isLoading: isUploadingFile }] = useUploadFileMutation();
  const [uploadImage, { isLoading: isUploadingImage }] = useUploadImageMutation();

  // Chat Composer State
  const [messageInput, setMessageInput] = useState("");
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    previewUrl?: string;
    type: 'IMAGE' | 'VIDEO' | 'PDF' | 'AUDIO' | 'FILE';
  } | null>(null);

  // Audio Voice Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Edit Message State
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [editMessageText, setEditMessageText] = useState("");

  // Optimistic messages for instantaneous local preview before network finish
  const [optimisticMessages, setOptimisticMessages] = useState<any[]>([]);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modals
  const [deletingMsgId, setDeletingMsgId] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<ChatMember | null>(null);
  const [deletingUserMsgsMember, setDeletingUserMsgsMember] = useState<ChatMember | null>(null);
  const [previewImageModal, setPreviewImageModal] = useState<string | null>(null);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    name: "",
    educationYear: "",
    description: "",
    status: "active" as "active" | "inactive"
  });

  // Add Members Modal State
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [addMemberYearFilter, setAddMemberYearFilter] = useState<string>("all");
  const [addMemberSearch, setAddMemberSearch] = useState<string>("");

  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: studentsResponse, isLoading: isStudentsLoading } = useGetStudentsQuery({ limit: 1000 });
  const allStudents: Student[] = Array.isArray(studentsResponse?.data) ? studentsResponse.data : [];
  const [addMembersToGroup, { isLoading: isAddingMembers }] = useAddMembersToGroupMutation();

  const group = groupData?.data;
  const messages = Array.isArray(messagesData?.data) ? messagesData.data : [];
  const members = Array.isArray(membersData?.data) ? membersData.data : [];
  const membersPagination = membersData?.pagination;

  const handleOpenAddMembers = () => {
    setSelectedStudentIds([]);
    setAddMemberYearFilter(group?.yearId || "all");
    setAddMemberSearch("");
    setIsAddMemberModalOpen(true);
  };

  const existingStudentIds = new Set(
    members.filter(m => m.status !== 'removed').map(m => m.studentId)
  );

  const addableStudents = allStudents.filter(s => {
    let matchesYear = true;
    if (addMemberYearFilter === "unassigned") {
      matchesYear = !s.yearId;
    } else if (addMemberYearFilter !== "all") {
      matchesYear = s.yearId === addMemberYearFilter;
    }

    const searchLower = addMemberSearch.toLowerCase().trim();
    const matchesSearch = !searchLower ||
      s.name?.toLowerCase().includes(searchLower) ||
      s.mobile?.includes(searchLower) ||
      s.email?.toLowerCase().includes(searchLower);
    return matchesYear && matchesSearch;
  });

  const toggleAddStudent = (id: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllAddable = () => {
    const unaddedIds = addableStudents
      .filter(s => !existingStudentIds.has(s.id))
      .map(s => s.id);
    setSelectedStudentIds(prev => Array.from(new Set([...prev, ...unaddedIds])));
  };

  const handleDeselectAllAddable = () => {
    setSelectedStudentIds([]);
  };

  const handleAddMembersSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStudentIds.length === 0) {
      toast.error("Please select at least one student to add.");
      return;
    }

    try {
      const res = await addMembersToGroup({
        groupId: groupId!,
        studentIds: selectedStudentIds
      }).unwrap();
      toast.success(res.message || "Students added to group successfully!");
      setIsAddMemberModalOpen(false);
      setSelectedStudentIds([]);
      refetchMembers();
      refetchGroup();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to add students to group");
    }
  };

  useEffect(() => {
    if (group) {
      setSettingsForm({
        name: group.name,
        educationYear: group.educationYear,
        description: group.description || "",
        status: group.status
      });
    }
  }, [group]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (activeTab === "chat") {
      messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
    }
  }, [messages.length, activeTab]);

  const handleProcessSelectedFile = (file: File) => {
    // Check size limit (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File size exceeds 50MB limit");
      return;
    }

    let type: 'IMAGE' | 'VIDEO' | 'PDF' | 'AUDIO' | 'FILE' = 'FILE';
    let previewUrl: string | undefined;

    if (file.type.startsWith("image/")) {
      type = 'IMAGE';
      previewUrl = URL.createObjectURL(file);
    } else if (file.type.startsWith("video/")) {
      type = 'VIDEO';
      previewUrl = URL.createObjectURL(file);
    } else if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      type = 'PDF';
    } else if (file.type.startsWith("audio/")) {
      type = 'AUDIO';
    }

    setSelectedFile({ file, previewUrl, type });
  };

  const handleSelectAttachmentCategory = (acceptType: string) => {
    setIsAttachmentOpen(false);
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptType;
    input.onchange = (e: any) => {
      const file = e.target?.files?.[0];
      if (file) handleProcessSelectedFile(file);
    };
    input.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleProcessSelectedFile(file);
  };

  // --- Voice Note Recording Handlers ---
  const startVoiceRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error("Audio recording is not supported in this browser.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingSeconds(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err: any) {
      console.error("Microphone access error:", err);
      toast.error("Microphone access required. Please allow mic permissions in browser.");
    }
  };

  const cancelVoiceRecording = () => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setRecordingSeconds(0);
    audioChunksRef.current = [];
  };

  const stopVoiceRecordingAndSend = () => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }

    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const fileName = `voice-note-${Date.now()}.webm`;

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setIsRecording(false);
      setRecordingSeconds(0);

      if (audioBlob.size < 200) {
        toast.error("Voice note recording was too short.");
        return;
      }

      const tempId = `opt-audio-${Date.now()}`;
      const audioBlobUrl = URL.createObjectURL(audioBlob);
      const optimisticMsg: any = {
        id: tempId,
        groupId: groupId!,
        senderId: "admin",
        senderType: "ADMIN",
        senderName: "Admin",
        messageType: "AUDIO",
        messageText: "🎤 Voice Note",
        fileUrl: audioBlobUrl,
        fileName: fileName,
        fileSize: audioBlob.size,
        mimeType: "audio/webm",
        createdAt: new Date().toISOString(),
        isOptimistic: true,
      };

      setOptimisticMessages(prev => [...prev, optimisticMsg]);

      try {
        const formData = new FormData();
        formData.append("file", audioBlob, fileName);
        const uploadRes = await uploadFile({ formData, folder: "chat" }).unwrap();

        await sendMessage({
          groupId: groupId!,
          messageText: "🎤 Voice Note",
          messageType: "AUDIO",
          fileUrl: uploadRes.url,
          fileName: fileName,
          fileSize: audioBlob.size,
          mimeType: "audio/webm",
        }).unwrap();

        refetchMessages();
        toast.success("Voice note sent successfully!");
      } catch (err: any) {
        toast.error(err?.data?.message || "Failed to send voice note");
      } finally {
        setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
      }
    };

    mediaRecorderRef.current.stop();
  };

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // --- Edit Message Handlers ---
  const handleOpenEditMessage = (msg: ChatMessage) => {
    setEditingMessage(msg);
    setEditMessageText(msg.messageText || "");
  };

  const handleSaveEditMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessage || !groupId) return;
    if (!editMessageText.trim()) {
      toast.error("Message text cannot be empty");
      return;
    }

    try {
      await editMessage({
        messageId: editingMessage.id,
        groupId,
        messageText: editMessageText.trim()
      }).unwrap();
      toast.success("Message edited successfully!");
      setEditingMessage(null);
      setEditMessageText("");
      refetchMessages();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to edit message");
    }
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!groupId) return;
    if (!messageInput.trim() && !selectedFile) return;

    const currentText = messageInput.trim();
    const currentFile = selectedFile;
    const tempId = `opt-${Date.now()}`;

    // Instant optimistic preview in chat stream
    const optimisticMsg: any = {
      id: tempId,
      groupId,
      senderId: "admin",
      senderType: "ADMIN",
      senderName: "Admin",
      messageType: currentFile?.type || 'TEXT',
      messageText: currentText || null,
      fileUrl: currentFile?.previewUrl || null,
      fileName: currentFile?.file.name || null,
      fileSize: currentFile?.file.size || null,
      mimeType: currentFile?.file.type || null,
      createdAt: new Date().toISOString(),
      isOptimistic: true,
    };

    setOptimisticMessages(prev => [...prev, optimisticMsg]);
    setMessageInput("");
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    try {
      let fileUrl: string | undefined;
      let fileName: string | undefined;
      let fileSize: number | undefined;
      let mimeType: string | undefined;
      let messageType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'PDF' | 'AUDIO' | 'FILE' = 'TEXT';

      if (currentFile) {
        const formData = new FormData();
        formData.append(currentFile.type === 'IMAGE' ? 'image' : 'file', currentFile.file);
        
        const uploadRes = currentFile.type === 'IMAGE' 
          ? await uploadImage({ formData, folder: 'chat' }).unwrap()
          : await uploadFile({ formData, folder: 'chat' }).unwrap();

        fileUrl = uploadRes.url;
        fileName = currentFile.file.name;
        fileSize = currentFile.file.size;
        mimeType = currentFile.file.type;
        messageType = currentFile.type;
      }

      await sendMessage({
        groupId,
        messageText: currentText || undefined,
        messageType,
        fileUrl,
        fileName,
        fileSize,
        mimeType
      }).unwrap();

      refetchMessages();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to send message");
    } finally {
      setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
    }
  };

  const handleDeleteMessage = async () => {
    if (!deletingMsgId || !groupId) return;
    try {
      await deleteMessage({ messageId: deletingMsgId, groupId }).unwrap();
      toast.success("Message deleted successfully!");
      setDeletingMsgId(null);
      refetchMessages();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete message");
    }
  };

  const handleToggleMemberStatus = async (member: ChatMember) => {
    if (!groupId) return;
    const newStatus = member.status === "active" ? "inactive" : "active";
    try {
      await updateMemberStatus({ groupId, memberId: member.id, status: newStatus }).unwrap();
      toast.success(`Member status updated to ${newStatus}!`);
      refetchMembers();
      refetchGroup();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update member status");
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!removingMember || !groupId) return;
    try {
      await removeMember({ groupId, memberId: removingMember.id }).unwrap();
      toast.success("Student removed from group!");
      setRemovingMember(null);
      refetchMembers();
      refetchGroup();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to remove member");
    }
  };

  const handleConfirmDeleteAllUserMessages = async () => {
    if (!deletingUserMsgsMember || !groupId) return;
    try {
      const res = await deleteAllMemberMessages({
        groupId,
        memberId: deletingUserMsgsMember.id,
        studentId: deletingUserMsgsMember.studentId
      }).unwrap();
      toast.success(res.message || "User messages deleted successfully!");
      setDeletingUserMsgsMember(null);
      refetchMessages();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete user messages");
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupId) return;
    try {
      await updateGroup({ id: groupId, ...settingsForm }).unwrap();
      toast.success("Group settings updated successfully!");
      refetchGroup();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update group");
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupId) return;
    if (!confirm("Are you sure you want to delete this group permanently?")) return;
    try {
      await deleteGroup(groupId).unwrap();
      toast.success("Group deleted successfully!");
      navigate("/chat-groups");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete group");
    }
  };

  if (isGroupLoading) {
    return (
      <div className="flex h-[80vh] items-center justify-center flex-col gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-xs font-medium text-muted-foreground">Loading chat group...</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-xl mx-auto p-12 text-center space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
        <h2 className="text-lg font-bold">Group Not Found</h2>
        <p className="text-xs text-muted-foreground">The requested chat group could not be located.</p>
        <Button onClick={() => navigate("/chat-groups")} className="rounded-xl">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Groups
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background/50 flex flex-col">
      {/* Top Header Bar */}
      <div className="border-b bg-card/70 backdrop-blur-md sticky top-0 z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/chat-groups")}
              className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center text-primary-foreground font-bold shadow-xs">
                <MessagesSquare className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-bold tracking-tight text-foreground">{group.name}</h1>
                  <Badge variant="outline" className="text-[10px] font-mono font-bold bg-muted/60">
                    {group.educationYear}
                  </Badge>
                  <Badge className={cn("text-[9px] uppercase font-bold", group.status === "active" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30" : "bg-muted text-muted-foreground")}>
                    {group.status}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {group.stats?.totalMembers || 0} enrolled students • {messages.length} messages loaded
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList className="rounded-xl h-10 p-1 bg-muted/60">
                <TabsTrigger value="chat" className="rounded-lg text-xs font-bold gap-1.5 px-3">
                  <MessagesSquare className="h-3.5 w-3.5" /> Live Chat
                </TabsTrigger>
                <TabsTrigger value="members" className="rounded-lg text-xs font-bold gap-1.5 px-3">
                  <Users className="h-3.5 w-3.5" /> Members ({group.stats?.totalMembers || 0})
                </TabsTrigger>
                <TabsTrigger value="settings" className="rounded-lg text-xs font-bold gap-1.5 px-3">
                  <Settings className="h-3.5 w-3.5" /> Settings
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8">
        {/* --- TAB 1: LIVE CHAT --- */}
        {activeTab === "chat" && (
          <Card className="border shadow-md bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden flex flex-col h-[calc(100vh-180px)]">
            {/* Messages Thread Container */}
            <div className="flex-1 p-4 sm:p-6 overflow-y-auto space-y-4 bg-muted/10">
              {isMessagesLoading ? (
                <div className="h-full flex flex-col items-center justify-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-xs font-medium text-muted-foreground">Loading chat messages...</p>
                </div>
              ) : (() => {
                const displayMessages = [
                  ...messages.filter(m => !m.isDeleted),
                  ...optimisticMessages
                ];

                if (displayMessages.length === 0) {
                  return (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8">
                      <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-3">
                        <MessagesSquare className="h-6 w-6" />
                      </div>
                      <h3 className="text-sm font-bold text-foreground">Welcome to {group.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                        Start the conversation! Post study announcements, notes PDFs, or interact with enrolled students.
                      </p>
                    </div>
                  );
                }

                return displayMessages.map((msg) => {
                  const isAdmin = msg.senderType === "ADMIN";
                  const isOptimistic = msg.isOptimistic;
                  const secureFileUrl = isOptimistic ? msg.fileUrl : getFileUrl(msg.fileUrl);

                  return (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex gap-3 group items-start",
                        isAdmin ? "flex-row-reverse" : "flex-row"
                      )}
                    >
                      {/* Avatar */}
                      <div
                        className={cn(
                          "h-8 w-8 rounded-xl shrink-0 flex items-center justify-center text-xs font-bold shadow-xs",
                          isAdmin
                            ? "bg-primary text-primary-foreground font-black ring-2 ring-primary/20"
                            : "bg-muted text-muted-foreground border"
                        )}
                      >
                        {isAdmin ? "A" : (msg.senderName || "Student").charAt(0).toUpperCase()}
                      </div>

                      {/* Message Bubble Content */}
                      <div className={cn("max-w-[80%] sm:max-w-[70%] space-y-1", isAdmin ? "items-end text-right" : "items-start text-left")}>
                        {/* Sender Header */}
                        <div className="flex items-center gap-2 px-1">
                          <span className="text-[11px] font-bold text-foreground">{msg.senderName || (isAdmin ? "Admin" : "Student")}</span>
                          <Badge
                            variant={isAdmin ? "default" : "secondary"}
                            className="text-[9px] uppercase px-1.5 py-0 font-extrabold"
                          >
                            {msg.senderType}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {format(new Date(msg.createdAt), "h:mm a")}
                          </span>
                        </div>

                        {/* Bubble Body */}
                        <div
                          className={cn(
                            "relative p-3.5 rounded-2xl text-xs leading-relaxed transition-all shadow-xs",
                            isAdmin
                              ? "bg-primary text-primary-foreground rounded-tr-xs"
                              : "bg-card border text-card-foreground rounded-tl-xs",
                            isOptimistic && "opacity-80"
                          )}
                        >
                          <div className="space-y-2">
                            {/* Rich Attachments Display */}
                            {secureFileUrl && (
                              <div className="space-y-2">
                                    {msg.messageType === "IMAGE" && (
                                      <div
                                        onClick={() => setPreviewImageModal(secureFileUrl)}
                                        className="rounded-xl overflow-hidden max-h-64 max-w-sm cursor-pointer border border-black/10 hover:opacity-95 transition-opacity"
                                      >
                                        <img src={secureFileUrl} alt="Attached image" className="object-cover w-full h-full" />
                                      </div>
                                    )}

                                    {msg.messageType === "PDF" && (
                                      <a
                                        href={secureFileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cn(
                                          "flex items-center gap-3 p-3 rounded-xl border transition-all",
                                          isAdmin
                                            ? "bg-white/10 hover:bg-white/20 text-white border-white/20"
                                            : "bg-muted/50 hover:bg-muted text-foreground border-border/50"
                                        )}
                                      >
                                        <div className="h-10 w-10 rounded-lg bg-red-500/20 text-red-500 flex items-center justify-center shrink-0">
                                          <FileText className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                          <p className="text-xs font-bold truncate">{msg.fileName || "Document.pdf"}</p>
                                          <p className="text-[10px] opacity-75">PDF Document • Click to View</p>
                                        </div>
                                        <Download className="h-4 w-4 shrink-0 opacity-80" />
                                      </a>
                                    )}

                                    {msg.messageType === "VIDEO" && (
                                      <div className="rounded-xl overflow-hidden max-w-sm border border-black/10">
                                        <video src={secureFileUrl} controls className="w-full h-auto rounded-xl" />
                                      </div>
                                    )}

                                    {msg.messageType === "AUDIO" && (
                                      <div className="p-2 rounded-xl bg-black/10">
                                        <audio src={secureFileUrl} controls className="w-full h-8" />
                                      </div>
                                    )}

                                    {msg.messageType === "FILE" && (
                                      <a
                                        href={secureFileUrl}
                                        download={msg.fileName || "file"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 p-2.5 rounded-xl bg-muted/40 border text-xs font-semibold"
                                      >
                                        <FileIcon className="h-4 w-4" />
                                        <span className="truncate">{msg.fileName || "Attachment"}</span>
                                        <Download className="h-3.5 w-3.5 ml-auto" />
                                      </a>
                                    )}
                                  </div>
                                )}

                              {/* Message Text */}
                              {msg.messageText && (
                                <p className="whitespace-pre-wrap break-words">{msg.messageText}</p>
                              )}
                            </div>

                          {/* Action Options Dropdown on hover */}
                          {!isOptimistic && (
                            <div className={cn("absolute top-1 opacity-0 group-hover:opacity-100 transition-opacity", isAdmin ? "-left-16" : "-right-16")}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full bg-card shadow-xs border text-muted-foreground hover:text-foreground">
                                    <MoreVertical className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align={isAdmin ? "end" : "start"} className="rounded-xl w-36">
                                  {isAdmin && (
                                    <DropdownMenuItem
                                      onClick={() => handleOpenEditMessage(msg)}
                                      className="cursor-pointer text-xs font-semibold text-foreground focus:text-foreground"
                                    >
                                      <Edit2 className="mr-2 h-3.5 w-3.5 text-primary" /> Edit Message
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => setDeletingMsgId(msg.id)}
                                    className="cursor-pointer text-xs font-semibold text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete Message
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
              <div ref={messagesEndRef} />
            </div>

            {/* Bottom Message Composer */}
            <div className="p-2.5 sm:p-3 border-t bg-card/95 backdrop-blur-md">
              {/* File Selected Attachment Preview */}
              {selectedFile && (
                <div className="mb-2 p-2 rounded-xl bg-muted/60 border border-primary/20 flex items-center justify-between text-xs animate-in fade-in-50">
                  <div className="flex items-center gap-2 min-w-0">
                    {selectedFile.type === 'IMAGE' && <ImageIcon className="h-4 w-4 text-emerald-500 shrink-0" />}
                    {selectedFile.type === 'PDF' && <FileText className="h-4 w-4 text-red-500 shrink-0" />}
                    {selectedFile.type === 'VIDEO' && <Video className="h-4 w-4 text-violet-500 shrink-0" />}
                    {selectedFile.type === 'AUDIO' && <Music className="h-4 w-4 text-amber-500 shrink-0" />}
                    {selectedFile.type === 'FILE' && <FileIcon className="h-4 w-4 text-sky-500 shrink-0" />}
                    <span className="font-bold truncate max-w-[200px] sm:max-w-xs">{selectedFile.file.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">({(selectedFile.file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedFile(null);
                    }}
                    className="h-6 w-6 rounded-full text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}

              {/* Recording Active Bar */}
              {isRecording ? (
                <div className="flex items-center justify-between gap-2 bg-destructive/10 border border-destructive/30 rounded-full py-1.5 px-3 sm:px-4 animate-in fade-in">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="h-3 w-3 rounded-full bg-destructive animate-ping shrink-0" />
                    <span className="text-xs font-bold text-destructive truncate">Recording Audio...</span>
                    <Badge variant="outline" className="font-mono text-xs border-destructive/30 text-destructive shrink-0">
                      {formatTimer(recordingSeconds)}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={cancelVoiceRecording}
                      className="h-9 w-9 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      title="Cancel Recording"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                    <Button
                      type="button"
                      onClick={stopVoiceRecordingAndSend}
                      className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-md flex items-center justify-center p-0 transition-transform active:scale-95"
                      title="Send Voice Note"
                    >
                      <Send className="h-4 w-4 translate-x-px" />
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="flex items-center gap-2 w-full">
                  {/* WhatsApp-Style Input Container Pill */}
                  <div className="flex-1 min-w-0 flex items-center bg-muted/40 dark:bg-muted/20 border border-input rounded-full px-2 py-1 shadow-xs focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/50 transition-all">
                    {/* WhatsApp-Style Attachment Popover */}
                    <Popover open={isAttachmentOpen} onOpenChange={setIsAttachmentOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={isSending || isUploadingFile || isUploadingImage}
                          className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground shrink-0 transition-transform active:scale-95"
                          title="Attach Files, Photos, PDF, Videos..."
                        >
                          <Paperclip className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent side="top" align="start" className="w-80 p-3 rounded-2xl shadow-xl border bg-card/95 backdrop-blur-xl">
                        <div className="grid grid-cols-3 gap-3 text-center">
                          {/* Photos & Images */}
                          <button
                            type="button"
                            onClick={() => handleSelectAttachmentCategory("image/*")}
                            className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-muted/60 transition-colors group cursor-pointer"
                          >
                            <div className="h-11 w-11 rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 group-hover:bg-emerald-500 group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                              <ImageIcon className="h-5 w-5" />
                            </div>
                            <span className="text-[11px] font-bold text-foreground">Photos</span>
                          </button>

                          {/* Videos */}
                          <button
                            type="button"
                            onClick={() => handleSelectAttachmentCategory("video/*")}
                            className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-muted/60 transition-colors group cursor-pointer"
                          >
                            <div className="h-11 w-11 rounded-2xl bg-violet-500/15 text-violet-600 dark:text-violet-400 group-hover:bg-violet-500 group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                              <Video className="h-5 w-5" />
                            </div>
                            <span className="text-[11px] font-bold text-foreground">Videos</span>
                          </button>

                          {/* PDF / Document */}
                          <button
                            type="button"
                            onClick={() => handleSelectAttachmentCategory(".pdf,application/pdf")}
                            className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-muted/60 transition-colors group cursor-pointer"
                          >
                            <div className="h-11 w-11 rounded-2xl bg-red-500/15 text-red-600 dark:text-red-400 group-hover:bg-red-500 group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                              <FileText className="h-5 w-5" />
                            </div>
                            <span className="text-[11px] font-bold text-foreground">PDF Document</span>
                          </button>

                          {/* Audio Files */}
                          <button
                            type="button"
                            onClick={() => handleSelectAttachmentCategory("audio/*")}
                            className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-muted/60 transition-colors group cursor-pointer"
                          >
                            <div className="h-11 w-11 rounded-2xl bg-amber-500/15 text-amber-600 dark:text-amber-400 group-hover:bg-amber-500 group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                              <Music className="h-5 w-5" />
                            </div>
                            <span className="text-[11px] font-bold text-foreground">Audio File</span>
                          </button>

                          {/* Other System Files */}
                          <button
                            type="button"
                            onClick={() => handleSelectAttachmentCategory("*/*")}
                            className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-muted/60 transition-colors group cursor-pointer"
                          >
                            <div className="h-11 w-11 rounded-2xl bg-sky-500/15 text-sky-600 dark:text-sky-400 group-hover:bg-sky-500 group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                              <FileIcon className="h-5 w-5" />
                            </div>
                            <span className="text-[11px] font-bold text-foreground">Other Files</span>
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* WhatsApp-Style Text Input */}
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      disabled={isSending || isUploadingFile || isUploadingImage}
                      className="flex-1 min-w-0 bg-transparent border-0 px-2 py-1.5 text-sm focus:outline-none placeholder:text-muted-foreground text-foreground"
                    />
                  </div>

                  {/* WhatsApp Circular Action Button: Send if message or file present, Mic if empty */}
                  {messageInput.trim() || selectedFile ? (
                    <Button
                      type="submit"
                      disabled={isSending || isUploadingFile || isUploadingImage}
                      className="h-10 w-10 sm:h-11 sm:w-11 rounded-full p-0 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md shrink-0 flex items-center justify-center transition-transform active:scale-95"
                      title="Send message"
                    >
                      {isSending || isUploadingFile || isUploadingImage ? (
                        <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 sm:h-5 sm:w-5 translate-x-px" />
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={startVoiceRecording}
                      disabled={isSending || isUploadingFile || isUploadingImage}
                      className="h-10 w-10 sm:h-11 sm:w-11 rounded-full p-0 bg-muted/70 hover:bg-primary text-foreground hover:text-primary-foreground border border-input shadow-xs shrink-0 flex items-center justify-center transition-all active:scale-95"
                      title="Record voice note"
                    >
                      <Mic className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>
                  )}
                </form>
              )}
            </div>
          </Card>
        )}

        {/* --- TAB 2: MEMBERS MANAGEMENT --- */}
        {activeTab === "members" && (
          <div className="space-y-6">
            <Card className="border shadow-xs bg-card/80 backdrop-blur-md rounded-2xl p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search member name, email or mobile..."
                    value={memberSearch}
                    onChange={(e) => { setMemberSearch(e.target.value); setMemberPage(1); }}
                    className="pl-9 h-10 rounded-xl text-xs"
                  />
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Select value={memberStatusFilter} onValueChange={(v) => { setMemberStatusFilter(v); setMemberPage(1); }}>
                    <SelectTrigger className="h-10 rounded-xl text-xs w-full sm:w-40">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="all">All Members</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                      <SelectItem value="removed">Removed Only</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    onClick={handleOpenAddMembers}
                    className="h-10 rounded-xl px-4 font-bold text-xs gap-1.5 shadow-sm shrink-0"
                  >
                    <UserPlus className="h-4 w-4" /> Add Members
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/40 border-b text-muted-foreground uppercase font-black tracking-wider text-[10px]">
                    <tr>
                      <th className="py-3.5 px-6">Student</th>
                      <th className="py-3.5 px-4">Contact Info</th>
                      <th className="py-3.5 px-4">College / State</th>
                      <th className="py-3.5 px-4">Joined Date</th>
                      <th className="py-3.5 px-4">Membership Status</th>
                      <th className="py-3.5 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {isMembersLoading ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-muted-foreground">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                          Loading group members...
                        </td>
                      </tr>
                    ) : members.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-muted-foreground">
                          <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
                          <p className="font-semibold text-foreground text-sm">No group members found</p>
                          <p className="text-xs text-muted-foreground mt-1 mb-4">Add students to this chat group to start the discussion.</p>
                          <Button onClick={handleOpenAddMembers} size="sm" className="rounded-xl font-bold text-xs">
                            <UserPlus className="mr-1.5 h-4 w-4" /> Add Students to Group
                          </Button>
                        </td>
                      </tr>
                    ) : (
                      members.map((m) => {
                        const st = m.student;
                        const studentId = st?.id || m.studentId;
                        return (
                          <tr
                            key={m.id}
                            onClick={() => {
                              if (studentId) navigate(`/students/${studentId}`);
                            }}
                            className="hover:bg-muted/40 transition-colors cursor-pointer group"
                            title="Click to view full student details"
                          >
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-xs">
                                  {st?.name ? st.name.charAt(0).toUpperCase() : "U"}
                                </div>
                                <div>
                                  <p className="font-bold text-foreground text-sm group-hover:text-primary transition-colors flex items-center gap-1.5">
                                    {st?.name || "Unnamed Student"}
                                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">{st?.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4 font-medium text-foreground">
                              {st?.mobile || <span className="text-muted-foreground italic">N/A</span>}
                            </td>
                            <td className="py-4 px-4 text-muted-foreground font-medium">
                              {st?.collegeName || st?.state || "Not specified"}
                            </td>
                            <td className="py-4 px-4 text-muted-foreground font-medium">
                              {m.joinedAt ? format(new Date(m.joinedAt), "MMM d, yyyy") : "N/A"}
                            </td>
                            <td className="py-4 px-4">
                              <Badge
                                className={cn(
                                  "text-[10px] font-bold uppercase tracking-wider",
                                  m.status === "active"
                                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                                    : m.status === "inactive"
                                    ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30"
                                    : "bg-destructive/15 text-destructive border border-destructive/30"
                                )}
                              >
                                {m.status === "active" && "Active"}
                                {m.status === "inactive" && "Inactive"}
                                {m.status === "removed" && "Removed (Locked)"}
                              </Badge>
                            </td>
                            <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl text-muted-foreground hover:text-foreground">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="rounded-xl w-48">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      if (studentId) navigate(`/students/${studentId}`);
                                    }}
                                    className="cursor-pointer text-xs font-semibold text-foreground"
                                  >
                                    <Eye className="mr-2 h-3.5 w-3.5 text-primary" /> View Student Details
                                  </DropdownMenuItem>
                                  {m.status !== "removed" && (
                                    <DropdownMenuItem onClick={() => handleToggleMemberStatus(m)} className="cursor-pointer text-xs font-semibold">
                                      {m.status === "active" ? (
                                        <>
                                          <UserX className="mr-2 h-3.5 w-3.5 text-amber-500" /> Deactivate Member
                                        </>
                                      ) : (
                                        <>
                                          <UserCheck className="mr-2 h-3.5 w-3.5 text-emerald-500" /> Activate Member
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => setDeletingUserMsgsMember(m)}
                                    className="cursor-pointer text-xs font-semibold text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete User Messages
                                  </DropdownMenuItem>
                                  {m.status !== "removed" && (
                                    <DropdownMenuItem
                                      onClick={() => setRemovingMember(m)}
                                      className="cursor-pointer text-xs font-semibold text-destructive focus:text-destructive"
                                    >
                                      <ShieldAlert className="mr-2 h-3.5 w-3.5" /> Remove from Group
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* --- TAB 3: GROUP SETTINGS --- */}
        {activeTab === "settings" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" /> Edit Group Properties
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Update the group title, description, and status.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSaveSettings} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Group Name</Label>
                        <Input
                          value={settingsForm.name}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, name: e.target.value }))}
                          className="h-11 rounded-xl"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Education Year</Label>
                        <Input
                          value={settingsForm.educationYear}
                          onChange={(e) => setSettingsForm(prev => ({ ...prev, educationYear: e.target.value }))}
                          className="h-11 rounded-xl"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold">Description</Label>
                      <Textarea
                        value={settingsForm.description}
                        onChange={(e) => setSettingsForm(prev => ({ ...prev, description: e.target.value }))}
                        className="min-h-[100px] rounded-xl text-xs"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold">Group Status</Label>
                      <Select value={settingsForm.status} onValueChange={(v: any) => setSettingsForm(prev => ({ ...prev, status: v }))}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="pt-4 flex justify-end">
                      <Button type="submit" disabled={isUpdatingGroup} className="rounded-xl h-10 px-6 text-xs font-bold shadow-sm">
                        {isUpdatingGroup ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Metrics & Danger Zone */}
            <div className="lg:col-span-4 space-y-6">
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl p-5 space-y-4">
                <h3 className="text-xs font-black uppercase tracking-wider text-muted-foreground">Group Metadata</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between py-1 border-b border-border/40">
                    <span className="text-muted-foreground">Total Enrolled:</span>
                    <span className="font-bold text-foreground">{group.stats?.totalMembers || 0} Students</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border/40">
                    <span className="text-muted-foreground">Active Members:</span>
                    <span className="font-bold text-emerald-600">{group.stats?.activeMembers || 0}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border/40">
                    <span className="text-muted-foreground">Total Messages:</span>
                    <span className="font-bold text-foreground">{group.stats?.totalMessages || 0}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-muted-foreground">Created On:</span>
                    <span className="font-medium text-foreground">{format(new Date(group.createdAt), "MMM d, yyyy")}</span>
                  </div>
                </div>
              </Card>

              <Card className="border border-destructive/20 bg-destructive/5 rounded-2xl p-5 space-y-3">
                <h3 className="text-xs font-black uppercase tracking-wider text-destructive">Danger Zone</h3>
                <p className="text-xs text-muted-foreground">
                  Permanently remove this chat group. All discussions and references will be purged.
                </p>
                <Button onClick={handleDeleteGroup} disabled={isDeletingGroup} variant="destructive" className="w-full rounded-xl text-xs font-bold h-10">
                  {isDeletingGroup ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Delete This Group"}
                </Button>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* DELETE MESSAGE CONFIRMATION */}
      <Dialog open={!!deletingMsgId} onOpenChange={(open) => !open && setDeletingMsgId(null)}>
        <DialogContent className="sm:max-w-[400px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" /> Soft-Delete Message?
            </DialogTitle>
            <DialogDescription className="text-xs">
              This message will be marked as deleted by administrator. Its original content and attachments will no longer be visible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3 gap-2">
            <Button variant="ghost" onClick={() => setDeletingMsgId(null)} className="rounded-xl h-10 text-xs font-semibold">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteMessage} disabled={isDeletingMessage} className="rounded-xl h-10 text-xs font-bold shadow-sm">
              {isDeletingMessage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REMOVE MEMBER CONFIRMATION */}
      <Dialog open={!!removingMember} onOpenChange={(open) => !open && setRemovingMember(null)}>
        <DialogContent className="sm:max-w-[420px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base text-destructive flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Remove Student from Group?
            </DialogTitle>
            <DialogDescription className="text-xs space-y-2">
              <p>
                Are you sure you want to remove <strong>{removingMember?.student?.name}</strong> from this chat group?
              </p>
              <p className="text-[11px] text-muted-foreground">
                Note: Automatic background synchronization will NOT re-add this student back to the group.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3 gap-2">
            <Button variant="ghost" onClick={() => setRemovingMember(null)} className="rounded-xl h-10 text-xs font-semibold">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemoveMember} className="rounded-xl h-10 text-xs font-bold shadow-sm">
              Confirm Removal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE ALL USER MESSAGES CONFIRMATION */}
      <Dialog open={!!deletingUserMsgsMember} onOpenChange={(open) => !open && setDeletingUserMsgsMember(null)}>
        <DialogContent className="sm:max-w-[420px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Delete All User Messages?
            </DialogTitle>
            <DialogDescription className="text-xs">
              This will soft-delete all messages sent by <strong>{deletingUserMsgsMember?.student?.name}</strong> in this chat group.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3 gap-2">
            <Button variant="ghost" onClick={() => setDeletingUserMsgsMember(null)} className="rounded-xl h-10 text-xs font-semibold">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeleteAllUserMessages} className="rounded-xl h-10 text-xs font-bold shadow-sm">
              Delete All Messages
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IMAGE PREVIEW MODAL */}
      <Dialog open={!!previewImageModal} onOpenChange={(open) => !open && setPreviewImageModal(null)}>
        <DialogContent className="sm:max-w-3xl p-2 rounded-2xl overflow-hidden bg-black/90 border-none">
          <div className="relative flex items-center justify-center min-h-[300px]">
            {previewImageModal && (
              <img src={previewImageModal} alt="Preview" className="max-h-[80vh] w-auto object-contain rounded-xl" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD MEMBERS TO GROUP MODAL */}
      <Dialog open={isAddMemberModalOpen} onOpenChange={setIsAddMemberModalOpen}>
        <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" /> Add Members to {group?.name || "Group"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Search and select students to add to this chat group.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddMembersSubmit} className="space-y-4 py-2">
            {/* Filter controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Select value={addMemberYearFilter} onValueChange={setAddMemberYearFilter}>
                  <SelectTrigger className="h-10 rounded-xl text-xs bg-background">
                    <SelectValue placeholder="Filter by Academic Year" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="all">All Students ({allStudents.length})</SelectItem>
                    {yearsData?.data?.map((y: any) => {
                      const count = allStudents.filter(s => s.yearId === y.id).length;
                      return (
                        <SelectItem key={y.id} value={y.id}>
                          {y.name} ({count})
                        </SelectItem>
                      );
                    })}
                    {allStudents.some(s => !s.yearId) && (
                      <SelectItem value="unassigned">
                        Unassigned ({allStudents.filter(s => !s.yearId).length})
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search student by name, mobile, email..."
                  value={addMemberSearch}
                  onChange={(e) => setAddMemberSearch(e.target.value)}
                  className="h-10 pl-9 rounded-xl text-xs bg-background"
                />
              </div>
            </div>

            {/* Selection stats & quick actions */}
            <div className="flex items-center justify-between text-xs py-1 border-b">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs font-semibold px-2.5 py-0.5 rounded-lg">
                  {selectedStudentIds.length} Selected
                </Badge>
                <span className="text-muted-foreground">
                  ({addableStudents.length} available)
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllAddable}
                  className="h-7 text-xs rounded-lg px-2.5 font-medium"
                >
                  Select All Available
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDeselectAllAddable}
                  className="h-7 text-xs rounded-lg px-2 text-muted-foreground"
                  disabled={selectedStudentIds.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>

            {/* Student checklist */}
            <div className="max-h-60 overflow-y-auto border rounded-xl divide-y bg-background">
              {isStudentsLoading ? (
                <div className="p-8 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading students...
                </div>
              ) : addableStudents.length === 0 ? (
                <div className="p-8 text-center text-xs text-muted-foreground">
                  No students found matching your filters.
                </div>
              ) : (
                addableStudents.map((st) => {
                  const isAlreadyMember = existingStudentIds.has(st.id);
                  const isChecked = selectedStudentIds.includes(st.id);
                  const yearName = yearsData?.data?.find((y: any) => y.id === st.yearId)?.name;

                  return (
                    <div
                      key={st.id}
                      onClick={() => !isAlreadyMember && toggleAddStudent(st.id)}
                      className={cn(
                        "flex items-center gap-3 p-3 transition-colors text-xs",
                        isAlreadyMember ? "opacity-60 bg-muted/20 cursor-not-allowed" : "hover:bg-muted/40 cursor-pointer",
                        isChecked && "bg-primary/5"
                      )}
                    >
                      <Checkbox
                        checked={isChecked || isAlreadyMember}
                        disabled={isAlreadyMember}
                        onCheckedChange={() => !isAlreadyMember && toggleAddStudent(st.id)}
                        className="rounded-md"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground truncate">{st.name}</span>
                          {yearName && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
                              {yearName}
                            </span>
                          )}
                          {isAlreadyMember && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/40 text-emerald-600 dark:text-emerald-400">
                              Already in Group
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {st.mobile || st.email || "No contact info"}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <DialogFooter className="pt-3 gap-2">
              <Button type="button" variant="ghost" onClick={() => setIsAddMemberModalOpen(false)} className="rounded-xl h-10 text-xs font-semibold">
                Cancel
              </Button>
              <Button type="submit" disabled={isAddingMembers || selectedStudentIds.length === 0} className="rounded-xl h-10 px-5 text-xs font-bold shadow-sm">
                {isAddingMembers ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                Add {selectedStudentIds.length} Student{selectedStudentIds.length === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Message Modal */}
      <Dialog open={!!editingMessage} onOpenChange={(open) => !open && setEditingMessage(null)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Edit2 className="h-5 w-5 text-primary" /> Edit Message
            </DialogTitle>
            <DialogDescription className="text-xs">
              Update the text content for this message.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveEditMessage} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium">Message Text</Label>
              <Textarea
                value={editMessageText}
                onChange={(e) => setEditMessageText(e.target.value)}
                placeholder="Type updated message..."
                rows={4}
                className="text-xs rounded-xl resize-none"
                autoFocus
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditingMessage(null)}
                className="rounded-xl h-9 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isEditingMsg || !editMessageText.trim()}
                className="rounded-xl h-9 px-4 text-xs font-bold"
              >
                {isEditingMsg ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Edit2 className="mr-2 h-4 w-4" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
